import PropTypes from 'prop-types'
import React from 'react'

import useFeatureAccessControlEnabled from 'hooks/useFeatureAccessControlEnabled'

/**
 * FeatureAccessControl
 *
 * Wrapper component that controls whether its children are rendered
 * based on a dynamic feature flag defined in the FeatureAccess Redux slice.
 *
 */
const FeatureAccessControl = ({ featureCode, fallback = <></>, children }) => {
  const isEnabled = useFeatureAccessControlEnabled(featureCode)

  // If the feature is disabled (or unknown), render the fallback
  if (!isEnabled) {
    return fallback
  }

  // When enabled, render the wrapped content
  return children
}

FeatureAccessControl.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  featureCode: PropTypes.string.isRequired,
}

/**
 * withFeatureAccessControl
 *
 * Higher-order component (HOC) version of FeatureAccessControl.
 *
 */
export const withFeatureAccessControl = featureCode => WrappedComponent => {
  const WithFeatureAccessControl = props => (
    <FeatureAccessControl featureCode={featureCode}>
      <WrappedComponent {...props} />
    </FeatureAccessControl>
  )

  WithFeatureAccessControl.displayName = `withFeatureAccessControl(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`

  return WithFeatureAccessControl
}

export default FeatureAccessControl
